import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TZ_OPTIONS = ['EST', 'CST', 'MST', 'PST', 'UTC', 'GMT/BST', 'CET'];

// Admin Moderation Center tab — configure the gate opening countdown
const GateCountdownSettings = () => {
  const [form, setForm] = useState({ enabled: false, event_date: '', event_time: '21:00', tz: 'EST', title: 'GRAND OPENING' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [targetUtc, setTargetUtc] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/gate/countdown`)
      .then(r => r.json())
      .then(d => {
        setForm({
          enabled: !!d.enabled,
          event_date: d.event_date || '',
          event_time: d.event_time || '21:00',
          tz: d.tz || 'EST',
          title: d.title || 'GRAND OPENING',
        });
        setTargetUtc(d.target_utc || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (enabledOverride = null) => {
    setSaving(true);
    setMsg(null);
    const payload = { ...form, enabled: enabledOverride !== null ? enabledOverride : form.enabled };
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/gate-countdown`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('beatseek_token')}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setForm(f => ({ ...f, enabled: payload.enabled }));
        setTargetUtc(data.target_utc || null);
        setMsg({ ok: true, text: payload.enabled ? '✅ Countdown is LIVE on the gate' : '⏸️ Countdown hidden from the gate' });
      } else {
        setMsg({ ok: false, text: data.detail || 'Failed to save' });
      }
    } catch {
      setMsg({ ok: false, text: 'Failed to save' });
    }
    setSaving(false);
  };

  if (loading) return <div className="text-gray-400 text-sm py-8 text-center">Loading countdown settings…</div>;

  const preview = targetUtc ? new Date(targetUtc) : null;
  const msLeft = preview ? preview.getTime() - Date.now() : null;

  return (
    <div className="space-y-4" data-testid="gate-countdown-settings">
      <div className="bg-cyan-900/15 border border-cyan-700/40 rounded-lg p-4">
        <h4 className="text-white font-semibold text-sm mb-1">⏳ Gate Opening Countdown</h4>
        <p className="text-xs text-gray-400 mb-4">
          Set the grand opening date and the beta gate shows a live countdown to everyone who lands on it.
          Change or hide it anytime — the gate updates instantly.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[11px] text-gray-400 mb-1">Headline</label>
            <input
              type="text"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              maxLength={60}
              className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="countdown-title-input"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 mb-1">Timezone</label>
            <select
              value={form.tz}
              onChange={e => set('tz', e.target.value)}
              className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="countdown-tz-select"
            >
              {TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 mb-1">Opening Date</label>
            <input
              type="date"
              value={form.event_date}
              onChange={e => set('event_date', e.target.value)}
              className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="countdown-date-input"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-400 mb-1">Opening Time</label>
            <input
              type="time"
              value={form.event_time}
              onChange={e => set('event_time', e.target.value)}
              className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
              data-testid="countdown-time-input"
            />
          </div>
        </div>

        {msg && (
          <div className={`text-xs mb-3 px-3 py-2 rounded-lg ${msg.ok ? 'bg-green-500/10 text-green-300 border border-green-500/30' : 'bg-red-500/10 text-red-300 border border-red-500/30'}`} data-testid="countdown-save-msg">
            {msg.text}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => save(true)}
            disabled={saving || !form.event_date}
            className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium"
            data-testid="countdown-save-btn"
          >
            {saving ? 'Saving…' : form.enabled ? '💾 Update Countdown' : '🚀 Set Live on Gate'}
          </button>
          {form.enabled && (
            <button
              onClick={() => save(false)}
              disabled={saving}
              className="bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-gray-200 px-4 py-2 rounded-lg text-sm font-medium"
              data-testid="countdown-disable-btn"
            >
              ⏸️ Hide from Gate
            </button>
          )}
        </div>
      </div>

      <div className={`rounded-lg p-4 border ${form.enabled ? 'bg-green-900/10 border-green-700/40' : 'bg-gray-800/40 border-gray-700/40'}`} data-testid="countdown-status">
        <div className="text-xs text-gray-400 mb-1">Current status</div>
        {form.enabled && preview ? (
          <div className="text-sm text-white">
            🟢 LIVE — counting down to <span className="text-cyan-300 font-semibold">{preview.toLocaleString('en-US', { weekday: 'short', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span> (your local time)
            {msLeft > 0 && (
              <span className="text-gray-400"> · {Math.floor(msLeft / 86400000)}d {Math.floor((msLeft % 86400000) / 3600000)}h {Math.floor((msLeft % 3600000) / 60000)}m to go</span>
            )}
            {msLeft <= 0 && <span className="text-green-300"> · 🎉 target reached — gate shows "DOORS ARE OPENING"</span>}
          </div>
        ) : (
          <div className="text-sm text-gray-400">⚫ Hidden — the gate shows no countdown</div>
        )}
      </div>
    </div>
  );
};

export default GateCountdownSettings;
